import { HighlightText } from "@/components/MedicalChronology/HighlightText";
import { Badge } from "@/components/ui/Badge";
import { cx } from "class-variance-authority";
import { Copy } from "lucide-react";
import React from "react";

import type { MedVisitDetailResponse } from "../../../types/medchron";
import { VisitCitationStackedList } from "./VisitCitationList";
import type { VisitCitationClickHandler, VisitDisplay } from "./types";

export interface VisitsTableRowProps {
  visit: VisitDisplay;
  details: MedVisitDetailResponse[];
  fileMapping: Record<string, string>;
  onCitationClick: VisitCitationClickHandler;
  formatVisitType: (id: string) => string;
  formatDetailCategory: (category: string, isCollapsed?: boolean) => string;
  formatDate: (d: string | null) => string;
  highlightQuery: string;
  editMode?: boolean;
  onEditModeRowClick?: (visitId: string) => void;
}

export const VisitsTableRow: React.FC<VisitsTableRowProps> = ({
  visit,
  details,
  fileMapping,
  onCitationClick,
  formatVisitType,
  formatDetailCategory,
  formatDate,
  highlightQuery,
  editMode,
  onEditModeRowClick,
}) => {
  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
    if (!editMode || !onEditModeRowClick) return;
    if (event.target instanceof Element && event.target.closest("button, [role='button']")) return;
    onEditModeRowClick(visit.visit_id);
  };

  const primaryCitation = visit.citations[0];
  const openPrimaryCitation = () =>
    primaryCitation && onCitationClick(primaryCitation, visit.visit_id);
  const isTitleCitationLink = Boolean(primaryCitation) && !editMode;
  const titleBlockProps = isTitleCitationLink
    ? {
        role: "button",
        tabIndex: 0,
        onClick: openPrimaryCitation,
        onKeyDown: (e: React.KeyboardEvent) => e.key === "Enter" && openPrimaryCitation(),
      }
    : {};
  const titleBlockClassName = cx(isTitleCitationLink && "group/title cursor-pointer");
  const titleClassName = cx(
    "font-bold text-black",
    isTitleCitationLink && "transition-colors group-hover/title:text-primary"
  );

  return (
    <tr
      data-visit-id={visit.visit_id}
      onClick={handleRowClick}
      className={cx(
        "group/row",
        editMode ? "hover:bg-primary-subtle" : "hover:bg-neutral-50/60",
        editMode && "visits-table-row-editable"
      )}
    >
      <td
        className={cx(
          "sticky left-0 z-10 min-w-[140px] whitespace-nowrap border-b border-l-2 border-b-neutral-200 border-l-transparent bg-card px-4 py-3 align-top text-sm font-semibold text-neutral-900",
          editMode
            ? "group-hover/row:border-l-primary group-hover/row:bg-primary-subtle"
            : "group-hover/row:bg-neutral-50/60"
        )}
      >
        {visit.isCollapsed ? (
          <>
            {visit.startDate ? (
              <HighlightText highlightQuery={highlightQuery} text={formatDate(visit.startDate)} />
            ) : (
              "Unknown"
            )}{" "}
            –{" "}
            {visit.endDate ? (
              <HighlightText highlightQuery={highlightQuery} text={formatDate(visit.endDate)} />
            ) : (
              "Unknown"
            )}
          </>
        ) : visit.visit_date ? (
          <HighlightText highlightQuery={highlightQuery} text={formatDate(visit.visit_date)} />
        ) : (
          "Unknown date"
        )}
      </td>
      <td
        className={cx(
          "min-w-[220px] border-b border-neutral-200 bg-card px-4 py-3 align-top text-sm text-neutral-900",
          editMode ? "group-hover/row:bg-primary-subtle" : "group-hover/row:bg-neutral-50/60"
        )}
      >
        <div className={titleBlockClassName} {...titleBlockProps}>
          <div className="flex flex-wrap items-center gap-1.5">
            {visit.isCollapsed && (
              <Badge tone="info" shape="tag">
                {visit.visitCount} visit{visit.visitCount !== 1 ? "s" : ""}
              </Badge>
            )}
            <span className={titleClassName}>
              <HighlightText
                highlightQuery={highlightQuery}
                text={formatVisitType(visit.visit_type_id)}
              />
            </span>
            {visit.visit_subtype && (
              <span className="text-neutral-500">
                (<HighlightText highlightQuery={highlightQuery} text={visit.visit_subtype} />)
              </span>
            )}
            {visit.sourceVersionName && !visit.isCollapsed && (
              <Badge
                tone="neutral"
                shape="tag"
                variant="outline"
                icon={<Copy size={10} />}
                className="gap-0.5 px-1 py-0 opacity-60"
              >
                Copied from {visit.sourceVersionName}
              </Badge>
            )}
          </div>
          <div className="mt-0.5 text-xs text-neutral-500">
            <HighlightText
              highlightQuery={highlightQuery}
              text={visit.provider_name ?? "Unknown"}
            />
            {visit.facility_name && (
              <span>
                {" "}
                (<HighlightText highlightQuery={highlightQuery} text={visit.facility_name} />)
              </span>
            )}
          </div>
        </div>
      </td>
      <td
        className={cx(
          "min-w-[280px] border-b border-neutral-200 bg-card px-4 py-3 align-top text-sm text-neutral-900",
          editMode ? "group-hover/row:bg-primary-subtle" : "group-hover/row:bg-neutral-50/60"
        )}
      >
        <div className="space-y-1">
          {details.map((detail) => (
            <div key={detail.visit_detail_category}>
              <span className="mr-1.5 text-2xs font-semibold uppercase tracking-micro text-black">
                <HighlightText
                  highlightQuery={highlightQuery}
                  text={formatDetailCategory(detail.visit_detail_category, visit.isCollapsed)}
                />
                :
              </span>
              <span className="whitespace-pre-line text-neutral-900">
                <HighlightText highlightQuery={highlightQuery} text={detail.visit_details} />
              </span>
            </div>
          ))}
        </div>
      </td>
      <td
        className={cx(
          "min-w-[220px] border-b border-neutral-200 bg-card px-4 py-3 align-top text-sm",
          editMode ? "group-hover/row:bg-primary-subtle" : "group-hover/row:bg-neutral-50/60"
        )}
      >
        <VisitCitationStackedList
          citations={visit.citations}
          fileMapping={fileMapping}
          onCitationClick={(citation) => onCitationClick(citation, visit.visit_id)}
          highlightQuery={highlightQuery}
        />
      </td>
    </tr>
  );
};
